import type { Hip3Market } from "@/types/hip3";
import type { BuildOiTreemapOptions, OiTreemapNode } from "@/types/treemap";
import { marketNotionalOi, marketSlug } from "@/lib/market-utils";

const DEFAULT_ROOT_NAME = "All Markets";
export const DEFAULT_MIN_PERCENTAGE_THRESHOLD = 0.015;
export const DEFAULT_MIN_ABSOLUTE_VALUE_USD = 5_000;

const categoryColorMap: Record<string, string> = {
  crypto: "#f5e9ff",
  equities: "#e5fce5",
  fx: "#e9efff",
  indices: "#fffbe9",
  commodities: "#eddfd1",
  general: "#ffe6d5",
};

const fallbackPalette = [
  "#dff3ff",
  "#fbe6d7",
  "#e8f8de",
  "#ece8ff",
  "#fde8ef",
  "#f7f0db",
  "#dff5f1",
  "#f7e3fb",
];

function isFinitePositive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function normalizePathPart(value?: string): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : "General";
}

function makeMarketLabel(market: Hip3Market): string {
  const base = market.baseAsset.toUpperCase();
  const quote = market.quoteAsset.toUpperCase();
  const provider = market.provider.toUpperCase();
  const slug = marketSlug(market);
  return `${base} / ${quote} · ${provider} (${slug})`;
}

function sumChildren(children: OiTreemapNode[]): number {
  return children.reduce((sum, child) => sum + child.value, 0);
}

function sortNodesByValue(nodes: OiTreemapNode[]): OiTreemapNode[] {
  return [...nodes].sort((a, b) => b.value - a.value);
}

function groupSmallLeaves(node: OiTreemapNode, minPct: number, minAbs: number): OiTreemapNode {
  if (!node.children?.length) return node;

  const processedChildren = node.children.map((child) => groupSmallLeaves(child, minPct, minAbs));
  let children = sortNodesByValue(processedChildren);
  const total = sumChildren(children);
  const threshold = Math.max(total * minPct, minAbs);

  const tinyLeaves: OiTreemapNode[] = [];
  const stableChildren: OiTreemapNode[] = [];

  for (const child of children) {
    const isLeaf = !child.children || child.children.length === 0;
    if (isLeaf && child.value < threshold) {
      tinyLeaves.push(child);
    } else {
      stableChildren.push(child);
    }
  }

  if (tinyLeaves.length >= 2) {
    stableChildren.push({
      name: `+${tinyLeaves.length} others`,
      value: sumChildren(tinyLeaves),
      children: sortNodesByValue(tinyLeaves),
      meta: {
        isGrouped: true,
        groupedCount: tinyLeaves.length,
      },
    });
  } else {
    stableChildren.push(...tinyLeaves);
  }

  children = sortNodesByValue(stableChildren);

  return {
    ...node,
    value: sumChildren(children),
    children,
  };
}

function categoryColor(name: string, index: number): string {
  const explicit = categoryColorMap[name.trim().toLowerCase()];
  if (explicit) return explicit;
  return fallbackPalette[index % fallbackPalette.length];
}

function nodePaletteColor(depth: number, index: number, categoryName?: string): string {
  if (depth === 1 && categoryName) {
    return categoryColor(categoryName, index);
  }

  // Rotate palette by depth so sibling sets don't look identical across levels.
  return fallbackPalette[(index + depth * 2) % fallbackPalette.length];
}

function withAssignedColors(root: OiTreemapNode): OiTreemapNode {
  if (!root.children?.length) return root;

  function paint(node: OiTreemapNode, depth: number): OiTreemapNode {
    if (!node.children?.length) return node;

    const children = node.children.map((child, index) => {
      const fill = nodePaletteColor(depth + 1, index, depth === 0 ? child.name : undefined);
      return paint(
        {
          ...child,
          fill,
        },
        depth + 1
      );
    });

    return {
      ...node,
      children,
    };
  }

  return paint(root, 0);
}

function dedupeName(name: string, seen: Map<string, number>): string {
  const count = seen.get(name) ?? 0;
  seen.set(name, count + 1);
  if (count === 0) return name;
  return `${name} #${count + 1}`;
}

export function buildOiCategoryTreemap(
  markets: Hip3Market[],
  options: BuildOiTreemapOptions = {}
): OiTreemapNode {
  const {
    rootName = DEFAULT_ROOT_NAME,
    groupSmallNodes = true,
    minPercentageThreshold = DEFAULT_MIN_PERCENTAGE_THRESHOLD,
    minAbsoluteValueUsd = DEFAULT_MIN_ABSOLUTE_VALUE_USD,
  } = options;

  const byCategory = new Map<string, Map<string, Hip3Market[]>>();

  for (const market of markets) {
    const oiUsd = marketNotionalOi(market);
    if (!isFinitePositive(oiUsd)) continue;

    const category = normalizePathPart(market.category);
    const asset = normalizePathPart(market.baseAsset).toUpperCase();

    if (!byCategory.has(category)) {
      byCategory.set(category, new Map<string, Hip3Market[]>());
    }

    const byAsset = byCategory.get(category);
    if (!byAsset) continue;

    if (!byAsset.has(asset)) {
      byAsset.set(asset, []);
    }

    byAsset.get(asset)?.push(market);
  }

  const categories: OiTreemapNode[] = [];

  for (const [category, byAsset] of byCategory.entries()) {
    const assets: OiTreemapNode[] = [];

    for (const [asset, assetMarkets] of byAsset.entries()) {
      const seenNames = new Map<string, number>();

      const marketsForAsset = assetMarkets
        .map((market) => {
          const oiUsd = marketNotionalOi(market);
          if (!isFinitePositive(oiUsd)) return null;

          const name = dedupeName(makeMarketLabel(market), seenNames);

          return {
            name,
            value: oiUsd,
            meta: {
              category,
              asset,
              provider: market.provider,
              marketId: market.marketId,
            },
          } satisfies OiTreemapNode;
        })
        .filter((market): market is OiTreemapNode => market !== null);

      if (!marketsForAsset.length) continue;

      const sortedMarkets = sortNodesByValue(marketsForAsset);
      assets.push({
        name: asset,
        value: sumChildren(sortedMarkets),
        children: sortedMarkets,
        meta: {
          category,
          asset,
        },
      });
    }

    if (!assets.length) continue;

    const sortedAssets = sortNodesByValue(assets);

    categories.push({
      name: category,
      value: sumChildren(sortedAssets),
      children: sortedAssets,
      meta: {
        category,
      },
    });
  }

  let root: OiTreemapNode = {
    name: rootName,
    value: 0,
    children: sortNodesByValue(categories),
  };

  if (root.children?.length) {
    root = {
      ...root,
      value: sumChildren(root.children),
    };
  }

  if (groupSmallNodes) {
    root = groupSmallLeaves(root, minPercentageThreshold, minAbsoluteValueUsd);
  }

  return withAssignedColors(root);
}

export function parseOiTreemapPathParam(raw: string | null): string[] {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((entry): entry is string => typeof entry === "string")
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  } catch {
    return [];
  }
}

export function stringifyOiTreemapPathParam(path: string[]): string {
  return JSON.stringify(
    path.map((entry) => entry.trim()).filter((entry) => entry.length > 0)
  );
}

export function getOiTreemapNodeByPath(
  root: OiTreemapNode,
  path: string[],
  maxDepth = Number.POSITIVE_INFINITY
): OiTreemapNode {
  let node = root;
  let depth = 0;

  for (const segment of path) {
    if (depth >= maxDepth) break;
    const next = node.children?.find((child) => child.name === segment);
    if (!next) break;
    node = next;
    depth += 1;
  }

  return node;
}

export function resolveOiTreemapPath(
  root: OiTreemapNode,
  path: string[],
  maxDepth = Number.POSITIVE_INFINITY
): string[] {
  const resolved: string[] = [];
  let node = root;

  for (const segment of path) {
    if (resolved.length >= maxDepth) break;

    const next = node.children?.find((child) => child.name === segment);
    if (!next) break;

    resolved.push(next.name);
    node = next;
  }

  return resolved;
}
